import type { ButtonSize } from '@timelyapp/react';
import { Button } from '@timelyapp/react';
import Popover from 'components/popovers/Popover';
import AppsListItem from 'components/services/AppsListItem';
import type { FactoryApp } from 'factories/apps';
import React, { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openAppEditor } from 'redux/modules/apps.actions';
import type { AppImports } from 'redux/selectors/apps';
import type { ThunkDispatch } from 'types/redux';
import {
  type IntegrationConfiguration,
  setConfiguration,
} from '~domains/integrations/redux/tic.integrations.slice';
import type { GetIntegrationsApiResponse } from '~redux/modules/tic/api-slice/GENERATED_ARTIFACT';
import { ticApi } from '~redux/modules/tic/api-slice/tic.apiSlice';
import { getContextualTicAccounts } from '~redux/modules/tic/tic.auth.selectors';
import { getHasTicIntegrationsSupport } from '~redux/selectors/accounts';
import { DUPLICATE_PROVIDER_MAP } from '~redux/selectors/duplicate-integrations';
import { getIsUserInExperimentalFeatureGroup } from '~redux/selectors/user';
import styles from './IntegrationsDropdown.module.scss';

type TicIntegration = GetIntegrationsApiResponse['data'][number];

/** Set of legacy app IDs that have a TIC equivalent */
const LEGACY_IDS_WITH_TIC = new Set(DUPLICATE_PROVIDER_MAP.map(e => e.legacyId));

interface Props {
  apps: AppImports;
  entity: string;
  size?: ButtonSize;
}

const TicListItem: React.FC<{
  integration: TicIntegration;
  connected: boolean;
  onClick: () => void;
}> = ({ integration, connected, onClick }) => (
  <li className={styles.ticItem} onClick={onClick}>
    <div className={styles.ticIcon}>
      {integration.icon_url && <img src={integration.icon_url} alt={integration.title} />}
    </div>
    <div className={styles.ticMeta}>
      <p className={styles.ticTitle}>{integration.title}</p>
      <p className={styles.ticDescription}>{connected ? 'Connected' : integration.description}</p>
    </div>
  </li>
);

const IntegrationsDropdown: React.FC<Props> = ({ apps, entity, size }) => {
  const popoverRef = useRef<Popover>(null);
  const dispatch = useDispatch<ThunkDispatch>();
  const hasTicSupport = useSelector(getHasTicIntegrationsSupport);
  const { workspace } = useSelector(getContextualTicAccounts);
  const isExperimentalUser = useSelector(getIsUserInExperimentalFeatureGroup);

  const accountId = workspace?.id;
  const { data } = ticApi.useGetIntegrationsQuery(
    { accountId: accountId! },
    { skip: !hasTicSupport || !accountId }
  );

  const ticIntegrations = useMemo(() => {
    return (data?.data || []).filter(i => isExperimentalUser || !i.experimental);
  }, [data, isExperimentalUser]);

  const { ticConnected, ticAvailable } = useMemo(() => {
    const connected: TicIntegration[] = [];
    const available: TicIntegration[] = [];
    for (const integration of ticIntegrations) {
      const isConnected =
        integration.account?.connection?.connected === true ||
        integration.personal?.connection?.connected === true;
      if (isConnected) {
        connected.push(integration);
      } else {
        available.push(integration);
      }
    }
    return { ticConnected: connected, ticAvailable: available };
  }, [ticIntegrations]);

  // Filter legacy "available" apps to remove those that have a TIC equivalent
  const filteredLegacyAvailable = useMemo(() => {
    if (!hasTicSupport || ticIntegrations.length === 0) return apps.available;
    return apps.available.filter(app => !LEGACY_IDS_WITH_TIC.has(app.id));
  }, [apps.available, hasTicSupport, ticIntegrations]);

  const handleAppClick = useCallback(
    (appId: number) => {
      dispatch(openAppEditor(appId));
    },
    [dispatch]
  );

  const handleTicClick = useCallback(
    (integration: TicIntegration, connected: boolean) => {
      popoverRef.current?.close();
      const config: Partial<IntegrationConfiguration> = {
        toolId: integration.id,
        scope: 'account',
        modal: connected ? 'manage' : 'setup_wizard',
      };
      dispatch(setConfiguration(config));
    },
    [dispatch]
  );

  const renderApps = (legacyApps: Array<FactoryApp>, title: string) => {
    if (legacyApps.length === 0) {
      return null;
    }

    return (
      <ul className={styles.appItems}>
        <h2>{title}</h2>
        {legacyApps.map(app => (
          <li className={styles.appItem} key={app.appId}>
            <AppsListItem variant="small" app={app} onClick={handleAppClick} />
          </li>
        ))}
      </ul>
    );
  };

  const renderTicItems = (integrations: TicIntegration[], connected: boolean) => {
    return integrations.map(integration => (
      <TicListItem
        key={integration.id}
        integration={integration}
        connected={connected}
        onClick={() => handleTicClick(integration, connected)}
      />
    ));
  };

  const hasConnected = apps.connected.length > 0 || ticConnected.length > 0;
  const hasAvailable = filteredLegacyAvailable.length > 0 || ticAvailable.length > 0;
  const hasDisconnected = apps.disconnected.length > 0;

  return (
    <Popover ref={popoverRef} target={<Button size={size}>Import</Button>}>
      <div>
        <div className={styles.header}>
          <h1 className={styles.title}>Import {entity}</h1>
          <p className={styles.description}>Import {entity} from other tools</p>
        </div>

        {hasConnected && (
          <ul className={styles.appItems}>
            <h2>Connected</h2>
            {apps.connected.map(app => (
              <li className={styles.appItem} key={app.appId}>
                <AppsListItem variant="small" app={app} onClick={handleAppClick} />
              </li>
            ))}
            {renderTicItems(ticConnected, true)}
          </ul>
        )}

        {hasAvailable && (
          <ul className={styles.appItems}>
            <h2>Available to connect</h2>
            {filteredLegacyAvailable.map(app => (
              <li className={styles.appItem} key={app.appId}>
                <AppsListItem variant="small" app={app} onClick={handleAppClick} />
              </li>
            ))}
            {renderTicItems(ticAvailable, false)}
          </ul>
        )}

        {hasDisconnected && renderApps(apps.disconnected, 'Disconnected')}
      </div>
    </Popover>
  );
};

export default IntegrationsDropdown;
